import type { CaseSource, DetailType, EntryType, Party, Role } from './types';

export const PARTY_LABEL: Record<Party, string> = {
  APPLICANT: 'Applicant',
  EMPLOYER: 'Employer',
  LANDLORD: 'Landlord',
  ACCOUNTANT: 'Accountant',
  PROPERTY: 'Property',
};

export const DETAIL_LABEL: Record<DetailType, string> = {
  NAME: 'Name',
  EMAIL: 'Email',
  PHONE: 'Phone',
  EMAIL_DOMAIN: 'Email domain',
  ADDRESS: 'Address',
  CONTACT_NAME: 'Contact name',
};

export const ENTRY_TYPE_LABEL: Record<EntryType, string> = {
  CONFIRMED_INVOLVED: 'Confirmed involved',
  DETAILS_USED: 'Details used',
};

export const SOURCE_LABEL: Record<CaseSource, string> = {
  APPLICATION: 'Application',
  MANUAL: 'Manual',
  BACKFILL: 'Backfill',
};

export const ROLE_LABEL: Record<Role, string> = {
  SUPER_ADMIN: 'Super admin',
  CLIENT_ADMIN: 'Client admin',
  FRAUD_ADMIN: 'Fraud admin',
  STAFF: 'Staff',
};

export const ROLE_HELP: Record<Role, string> = {
  SUPER_ADMIN: 'Manages every client, their API keys and users. Sees all data.',
  CLIENT_ADMIN: "Manages this client's users, plus everything a fraud admin can do.",
  FRAUD_ADMIN: 'Manual entries, amend, end or remove entries, reversals and backfill imports.',
  STAFF: 'View and search the register, record fraud, download documents.',
};

/** Which details each party can hold (mirrors the service's rules). */
export const PARTY_DETAILS: Record<Party, DetailType[]> = {
  APPLICANT: ['NAME', 'EMAIL', 'PHONE', 'ADDRESS'],
  EMPLOYER: ['NAME', 'CONTACT_NAME', 'EMAIL', 'PHONE', 'EMAIL_DOMAIN', 'ADDRESS'],
  LANDLORD: ['NAME', 'EMAIL', 'PHONE', 'ADDRESS'],
  ACCOUNTANT: ['NAME', 'CONTACT_NAME', 'EMAIL', 'PHONE', 'ADDRESS'],
  PROPERTY: ['ADDRESS'],
};

export function sourceText(c: { source: CaseSource; sourceReference: string | null; reason?: string | null }) {
  if (c.source === 'APPLICATION') return `Application ${c.sourceReference ?? ''}`.trim();
  if (c.source === 'MANUAL') return 'Manual entry';
  return c.sourceReference ? `Backfill · ${c.sourceReference}` : 'Backfill';
}

const dateFmt = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
const dateTimeFmt = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export const formatDate = (iso: string | null | undefined) => (iso ? dateFmt.format(new Date(iso)) : '—');
export const formatDateTime = (iso: string | null | undefined) => (iso ? dateTimeFmt.format(new Date(iso)) : '—');

export function formatBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
